import { RedlineData, UpdateDetailVersion } from '@ditto/shared';
import { RedlineChaptersPanel } from './RedlineChaptersPanel';
import { Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import { Button } from '@/components/ui/button';
import { Dropzone } from '@/components/ui/dropzone';
import { useVersionFileUploadService } from '@/services/regulatoryWatch';
import { Download, FileText, Loader2 } from 'lucide-react';
import { FC } from 'react';

const PdfViewerContent: FC<{
  fileUrl: string;
}> = ({ fileUrl }) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  return (
    <div style={{ height: '100%', overflow: 'hidden' }}>
      <Viewer
        fileUrl={fileUrl}
        plugins={[defaultLayoutPluginInstance]}
        defaultScale={1}
      />
    </div>
  );
};

// Simple PDF viewer for update files (amendments)
export const UpdateFileContent: FC<{ fileUrl: string }> = ({ fileUrl }) => {
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  return (
    <div style={{ height: '100%', overflow: 'hidden' }}>
      <Viewer
        fileUrl={fileUrl}
        plugins={[defaultLayoutPluginInstance]}
        defaultScale={1}
      />
    </div>
  );
};

const VersionContent: FC<{
  version: UpdateDetailVersion | null;
  label: string;
}> = ({ version, label }) => {
  const { uploadAndAttach, isUploading } = useVersionFileUploadService();

  if (!version) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-neutral-500">
        <FileText size={48} className="text-neutral-300" />
        <p>No {label} associated with this update</p>
      </div>
    );
  }

  const fileUrl =
    version.sharedFile?.signedUrl ?? version.workspaceFile?.signedUrl;

  if (fileUrl) {
    return <PdfViewerContent fileUrl={fileUrl} />;
  }

  if (version.content) {
    return (
      <div className="overflow-auto h-full p-4 prose prose-sm max-w-none">
        <div dangerouslySetInnerHTML={{ __html: version.content }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 gap-6">
      <div className="text-center text-neutral-500">
        <FileText size={40} className="mx-auto mb-3 text-neutral-300" />
        <p className="text-sm">
          This version has no available content. Upload a PDF of this version if
          you have it.
        </p>
      </div>
      <Dropzone
        onFilesSelected={files => {
          const file = files[0];
          if (file) {
            uploadAndAttach(file, version.id);
          }
        }}
        isLoading={isUploading}
        title="Upload PDF"
        subtitle="Drag and drop a PDF file or click to browse"
        accept=".pdf,application/pdf"
        multiple={false}
        className="w-full max-w-md"
      />
    </div>
  );
};

const RedlineContent: FC<{
  redline: RedlineData;
}> = ({ redline }) => {
  if (!redline || !redline.excelFileUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-neutral-500">
        <FileText size={48} className="text-neutral-300" />
        <p className="text-sm">No redline available for this update.</p>
      </div>
    );
  }

  if (redline.status === 'generating') {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-neutral-500">
        <Loader2 size={48} className="text-neutral-400 animate-spin" />
        <p className="text-sm">Generating redline...</p>
        <p className="text-xs text-neutral-400">
          This may take several minutes for large documents.
        </p>
      </div>
    );
  }

  const hasChapters = redline.chapters.length > 0;

  return (
    <div className="h-full flex">
      {hasChapters && (
        <div className="w-[480px] shrink-0 border-r border-neutral-200 overflow-hidden">
          <RedlineChaptersPanel chapters={redline.chapters} />
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center justify-end gap-2 px-4 py-2 border-b">
          {redline.excelFileUrl && (
            <a href={redline.excelFileUrl} download="redline.xlsx">
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Download Excel
              </Button>
            </a>
          )}
        </div>
        {redline.excelFileUrl && (
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(redline.excelFileUrl)}`}
            className="flex-1 w-full border-0"
            title="Redline Excel preview"
          />
        )}
      </div>
    </div>
  );
};

type ContentPanelProps = {
  activeTab: string;
  currentVersion: UpdateDetailVersion | null;
  previousVersion: UpdateDetailVersion | null;
  redline: RedlineData;
};

export const ContentPanel: FC<ContentPanelProps> = ({
  activeTab,
  currentVersion,
  previousVersion,
  redline,
}) => {
  if (activeTab === 'latest') {
    return (
      <div className="h-full overflow-hidden">
        <VersionContent version={currentVersion} label="reference version" />
      </div>
    );
  }

  if (activeTab === 'previous') {
    return (
      <div className="h-full overflow-hidden">
        <VersionContent version={previousVersion} label="previous version" />
      </div>
    );
  }

  if (activeTab === 'redline') {
    return (
      <div className="h-full overflow-hidden">
        <RedlineContent redline={redline} />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 text-neutral-500">
      <FileText size={48} className="text-neutral-300" />
      <p>Coming soon</p>
    </div>
  );
};
